import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './Shows.css';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo, faClockRotateLeft } from '@fortawesome/free-solid-svg-icons';

const API_BASE = process.env.REACT_APP_FRONTPAGE_API || 'http://localhost:5010/api';
const ASSET_BASE = process.env.REACT_APP_FRONTPAGE_ASSET_BASE || API_BASE.replace(/\/api\/?$/, '');

const FALLBACK_UPCOMING = [
  {
    title: 'Parkteatret',
    subtitle: 'DØDSEGO pres. "ALTIOSLO" - releasefest',
    date: '2026-03-25',
    city: 'Oslo',
    venue: 'Norway',
    description: 'Celebrate the ALTIOSLO release with a full-scale show in Oslo.',
    link: 'https://www.parkteatret.no/arrangement/dodsego',
    imagePath: '/images/POSTER_1080x1350_DDSEGO.png',
  },
];

const resolveMediaUrl = (path) => {
  if (!path) {
    return '';
  }
  if (/^https?:\/\//i.test(path)) {
    return path;
  }
  if (path.startsWith('/')) {
    if (path.startsWith('/images/') || path.startsWith('/video/') || path.startsWith('/assets/') || path.startsWith('/static/')) {
      return path;
    }
    return `${ASSET_BASE}${path}`;
  }
  return `${ASSET_BASE}/${path.replace(/^\/+/, '')}`;
};

const resolveOptimizedImageUrl = (path) => {
  const resolved = resolveMediaUrl(path);
  if (!resolved) {
    return '';
  }
  if (/\/bibliotek\/optimized\//i.test(resolved) || /_opt\.webp(\?.*)?$/i.test(resolved)) {
    return resolved;
  }
  if (/\/bibliotek\/originals\//i.test(resolved)) {
    return resolved
      .replace(/\/bibliotek\/originals\//i, '/bibliotek/optimized/')
      .replace(/\.[a-z0-9]+(\?.*)?$/i, '.webp$1');
  }
  const match = resolved.match(/^(.*)\.[a-z0-9]+(\?.*)?$/i);
  if (match && !/\.webp(\?.*)?$/i.test(resolved)) {
    return `${match[1]}_opt.webp${match[2] || ''}`;
  }
  return resolved;
};

const formatDate = (dateString) => {
  if (!dateString) {
    return null;
  }
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
};

const formatMeta = (event) => {
  const parts = [formatDate(event.date), event.city, event.venue].filter(Boolean);
  return parts.join(' • ');
};

const Shows = () => {
  const [events, setEvents] = useState(null);
  const [imageReadyById, setImageReadyById] = useState({});

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API_BASE}/events`);
      setEvents(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching events:', error);
      setEvents([]);
    }
  };

  const now = useMemo(() => new Date(), []);

  const markImageReady = useCallback((key) => {
    if (!key) return;
    setImageReadyById((prev) => {
      if (prev[key]) return prev;
      return { ...prev, [key]: true };
    });
  }, []);

  const { upcoming, past } = useMemo(() => {
    const source = Array.isArray(events) ? events : [];
    const upcomingEvents = [];
    const pastEvents = [];
    for (const event of source) {
      const eventDate = event?.date ? new Date(event.date) : null;
      if (eventDate && !Number.isNaN(eventDate.getTime()) && eventDate < now) {
        pastEvents.push(event);
      } else {
        upcomingEvents.push(event);
      }
    }
    pastEvents.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
    return { upcoming: upcomingEvents, past: pastEvents };
  }, [events, now]);

  const upcomingList = upcoming.length > 0 ? upcoming : FALLBACK_UPCOMING;
  const pastList = past;

  return (
    <div className="shows-background">
      <div className="banner" />
      <div className="shows-wrapper">
        <div className="shows-container">
          <section className="shows-section">
            <div className="shows-stack">
              {upcomingList.map((event, index) => {
                const meta = formatMeta(event);
                const key = event._id || `${event.title || 'event'}-${index}`;
                const image = resolveOptimizedImageUrl(event.imagePath);
                const imageFallback = resolveMediaUrl(event.imagePath);
                const imageKey = `upcoming-${key}`;
                const imageReady = !!imageReadyById[imageKey];
                return (
                  <article className="shows-card" key={key}>
                    <div className="shows-card__body">
                      {event.title && <h3 className="shows-card__title">{event.title}</h3>}
                      {event.subtitle && <p className="shows-card__subtitle">{event.subtitle}</p>}
                      {meta && <p className="shows-card__meta">{meta}</p>}
                      {event.description && (
                        <p className="shows-card__description">{event.description}</p>
                      )}
                    </div>
                    {image && (
                      <div className={`shows-card__media shows-card__media--concert shows-card__media--with-overlay${imageReady ? ' is-ready' : ''}`} aria-busy={!imageReady}>
                        {!imageReady && (
                          <div className="shows-card__media-loading" aria-hidden="true">
                            <span className="shows-card__media-loading-text">Loading image...</span>
                          </div>
                        )}
                        <img
                          className="shows-card__image"
                          src={image}
                          alt={event.title || 'Event poster'}
                          loading="lazy"
                          decoding="async"
                          onLoad={() => markImageReady(imageKey)}
                          onError={(e) => {
                            if (!imageFallback || e.currentTarget.dataset.fallbackApplied) {
                              markImageReady(imageKey);
                              return;
                            }
                            e.currentTarget.dataset.fallbackApplied = '1';
                            e.currentTarget.src = imageFallback;
                          }}
                        />
                        {event.link && (
                          <div className="shows-card__actions shows-card__actions--overlay shows-card__actions--overlay-right">
                            <a
                              href={event.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="shows-card__cta shows-card__cta--icon"
                              aria-label="More information"
                              title="More information"
                            >
                              <FontAwesomeIcon icon={faCircleInfo} className="shows-card__cta-icon" aria-hidden="true" />
                            </a>
                          </div>
                        )}
                      </div>
                    )}
                    {!image && event.link && (
                      <div className="shows-card__actions">
                        <a
                          href={event.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="shows-card__cta shows-card__cta--icon"
                          aria-label="More information"
                          title="More information"
                        >
                          <FontAwesomeIcon icon={faCircleInfo} className="shows-card__cta-icon" aria-hidden="true" />
                        </a>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
            {pastList.length > 0 && (
              <div className="shows-subsection">
                <header className="shows-subsection__header shows-subsection__header--icon">
                  <span className="shows-subsection__icon" role="img" aria-label="Past highlights">
                    <FontAwesomeIcon icon={faClockRotateLeft} />
                  </span>
                </header>
                <div className="shows-stack shows-stack--compact">
                  {pastList.map((event, index) => {
                    const key = event._id || `${event.title || 'past'}-${index}`;
                    const eventDate = formatDate(event.date);
                    const image = resolveOptimizedImageUrl(event.imagePath);
                    const imageFallback = resolveMediaUrl(event.imagePath);
                    const imageKey = `past-${key}`;
                    const imageReady = !!imageReadyById[imageKey];
                    return (
                      <article className="shows-card shows-card--compact" key={key}>
                        <div className="shows-card__body">
                          {event.title && <h4 className="shows-card__title">{event.title}</h4>}
                          {eventDate && <p className="shows-card__meta">{eventDate}</p>}
                        </div>
                        {image && (
                          <div className={`shows-card__media shows-card__media--compact shows-card__media--concert shows-card__media--with-overlay${imageReady ? ' is-ready' : ''}`} aria-busy={!imageReady}>
                            {!imageReady && (
                              <div className="shows-card__media-loading" aria-hidden="true">
                                <span className="shows-card__media-loading-text">Loading image...</span>
                              </div>
                            )}
                            <img
                              className="shows-card__image"
                              src={image}
                              alt={event.title || 'Event poster'}
                              loading="lazy"
                              decoding="async"
                              onLoad={() => markImageReady(imageKey)}
                              onError={(e) => {
                                if (!imageFallback || e.currentTarget.dataset.fallbackApplied) {
                                  markImageReady(imageKey);
                                  return;
                                }
                                e.currentTarget.dataset.fallbackApplied = '1';
                                e.currentTarget.src = imageFallback;
                              }}
                            />
                            <div className="shows-card__actions shows-card__actions--overlay">
                              {event.link ? (
                                <a
                                  href={event.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="shows-card__cta shows-card__cta--icon"
                                  aria-label="More information"
                                  title="More information"
                                >
                                  <FontAwesomeIcon icon={faCircleInfo} className="shows-card__cta-icon" aria-hidden="true" />
                                </a>
                              ) : (
                                <span
                                  className="shows-card__cta shows-card__cta--icon shows-card__cta--disabled"
                                  aria-disabled="true"
                                  aria-label="More information"
                                >
                                  <FontAwesomeIcon icon={faCircleInfo} className="shows-card__cta-icon" aria-hidden="true" />
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        {!image && (
                          <div className="shows-card__actions">
                            {event.link ? (
                              <a
                                href={event.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="shows-card__cta shows-card__cta--icon"
                                aria-label="More information"
                                title="More information"
                              >
                                <FontAwesomeIcon icon={faCircleInfo} className="shows-card__cta-icon" aria-hidden="true" />
                              </a>
                            ) : (
                              <span
                                className="shows-card__cta shows-card__cta--icon shows-card__cta--disabled"
                                aria-disabled="true"
                                aria-label="More information"
                              >
                                <FontAwesomeIcon icon={faCircleInfo} className="shows-card__cta-icon" aria-hidden="true" />
                              </span>
                            )}
                          </div>
                        )}
                      </article>
                    );
                  })}
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
      <div className="banner" />
    </div>
  );
};

export default Shows;